import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, Plus, Trash2, ClipboardList, Loader, AlertCircle, Copy } from "lucide-react";
import { CATEGORIES } from "../constants";
import { api } from "../api";

const FIELD_TYPES = [
  { value: "text", label: "Texto corto" },
  { value: "textarea", label: "Texto largo" },
  { value: "number", label: "Numero" },
  { value: "date", label: "Fecha" },
  { value: "select", label: "Seleccion (opciones)" },
  { value: "file", label: "Archivo adjunto" },
  { value: "boolean", label: "Si / No" },
];

const DATE_CALCULATIONS = [
  { value: "", label: "Sin cálculo automático" },
  { value: "age", label: "Calcular edad" },
  { value: "seniority", label: "Calcular antigüedad" },
  { value: "elapsed", label: "Calcular tiempo transcurrido" },
];

function newFieldId() {
  return `f_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

function emptyField() {
  return {
    id: newFieldId(),
    label: "",
    description: "",
    type: "text",
    required: false,
    sensitive: false,
    options: [],
    optionsText: "",
    dateCalculation: "",
  };
}

export default function FormBuilderModal({ form, onClose, onSaved }) {
  const isEdit = !!form;
  const [name, setName] = useState(form?.name || "");
  const [description, setDescription] = useState(form?.description || "");
  const [category, setCategory] = useState(form?.category || CATEGORIES[0]);
  const [moduleId, setModuleId] = useState(form?.module_id || "");
  const [modules, setModules] = useState([]);
  useEffect(() => { api.getFormModules().then(data => setModules(Array.isArray(data) ? data : [])); }, []);
  const [fields, setFields] = useState(
    form?.fields?.length
      ? form.fields.map((field) => ({
          ...field,
          options: field.options || [],
          optionsText: (field.options || []).join(", "),
          dateCalculation: field.dateCalculation || "",
        }))
      : [emptyField()]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [duplicateNotice, setDuplicateNotice] = useState("");
  const allRequired = fields.length > 0 && fields.every((field) => field.required);

  const updateField = (id, changes) => {
    setFields((prev) => prev.map((field) => (field.id === id ? { ...field, ...changes } : field)));
  };

  const addField = () => setFields((prev) => [...prev, emptyField()]);
  const removeField = (id) => setFields((prev) => prev.filter((field) => field.id !== id));
  const setAllRequired = (required) => {
    setFields((prev) => prev.map((field) => ({ ...field, required })));
  };
  const duplicateField = (id) => {
    setFields((prev) => {
      const index = prev.findIndex((field) => field.id === id);
      if (index === -1) return prev;
      const source = prev[index];
      const copy = {
        ...source,
        id: newFieldId(),
      };
      return [...prev.slice(0, index + 1), copy, ...prev.slice(index + 1)];
    });
    setDuplicateNotice("ESTAS DUPLICANDO UNA PREGUNTA");
    window.setTimeout(() => setDuplicateNotice(""), 2500);
  };

  const valid = name.trim() && category && fields.length > 0 &&
    fields.every((field) => {
      if (!field.label.trim()) return false;
      if (field.type !== "select") return true;
      return field.optionsText.split(",").map((item) => item.trim()).filter(Boolean).length > 0;
    });

  const handleSave = async () => {
    if (!valid) return;
    setSaving(true);
    setError(null);

    try {
      const body = {
        name,
        description,
        category,
        module_id: moduleId || null,
        fields: fields.map(({ optionsText, ...field }) => ({
          ...field,
          options: field.type === "select"
            ? optionsText.split(",").map((item) => item.trim()).filter(Boolean)
            : [],
          dateCalculation: field.type === "date" ? (field.dateCalculation || "") : "",
        })),
      };

      const result = isEdit ? await api.updateForm(form.id, body) : await api.createForm(body);
      if (result.error) throw new Error(result.error);
      onSaved(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <motion.div
        className="modal modal-large"
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 12 }}
        transition={{ type: "spring", stiffness: 380, damping: 30, mass: 0.7 }}
      >
        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "#FDF2F8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ClipboardList size={18} style={{ color: "#DB2777" }} />
            </div>
            <h2>{isEdit ? "Editar formulario" : "Nuevo formulario"}</h2>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-form" style={{ maxHeight: "65vh", overflowY: "auto" }}>
          <div className="form-row two-col">
            <div>
              <label>Nombre del formulario *</label>
              <input
                className="form-input"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Ej: Perfil sociodemografico"
              />
            </div>
            <div>
              <label>Carpeta destino *</label>
              <select className="form-input" value={category} onChange={(event) => setCategory(event.target.value)}>
                {CATEGORIES.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <label>Sección / módulo</label>
            <select className="form-input" value={moduleId} onChange={(event) => setModuleId(event.target.value)}>
              <option value="">Sin sección</option>
              {modules.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </div>

          <div className="form-row">
            <label>Descripcion</label>
            <textarea
              className="form-input form-textarea"
              rows={2}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Que informacion se esta recolectando y para que"
            />
          </div>

          <div className="form-row">
            <label>Campos del formulario</label>
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 12,
                fontSize: "0.82rem",
                color: "var(--text-muted)",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={allRequired}
                onChange={(event) => setAllRequired(event.target.checked)}
              />
              Todo obligatorio
            </label>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: 10,
                    padding: 12,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    background: "var(--bg)",
                  }}
                >
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: "0.72rem", color: "var(--text-muted)", flexShrink: 0 }}>#{index + 1}</span>
                    <input
                      className="form-input"
                      style={{ flex: 1 }}
                      value={field.label}
                      placeholder="Etiqueta del campo"
                      onChange={(event) => updateField(field.id, { label: event.target.value })}
                    />
                    <select
                      className="form-input"
                      style={{ maxWidth: 170 }}
                      value={field.type}
                      onChange={(event) => updateField(field.id, {
                        type: event.target.value,
                        options: event.target.value === "select" ? field.options : [],
                        optionsText: event.target.value === "select" ? field.optionsText : "",
                        dateCalculation: event.target.value === "date" ? field.dateCalculation : "",
                      })}
                    >
                      {FIELD_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                    </select>
                    <button
                      className="btn-icon"
                      onClick={() => duplicateField(field.id)}
                      title="Duplicar campo"
                    >
                      <Copy size={15} />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => removeField(field.id)}
                      disabled={fields.length === 1}
                      title="Quitar campo"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>

                  <textarea
                    className="form-input form-textarea"
                    rows={2}
                    value={field.description || ""}
                    placeholder="Descripcion de ayuda para esta pregunta (opcional)"
                    onChange={(event) => updateField(field.id, { description: event.target.value })}
                  />

                  {field.type === "select" && (
                    <input
                      className="form-input"
                      value={field.optionsText}
                      placeholder="Opciones separadas por coma (ej: Operario, Administrativo)"
                      onChange={(event) => updateField(field.id, { optionsText: event.target.value })}
                    />
                  )}

                  {field.type === "date" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <label style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                        Cálculo automático
                      </label>
                      <select
                        className="form-input"
                        value={field.dateCalculation || ""}
                        onChange={(event) => updateField(field.id, { dateCalculation: event.target.value })}
                      >
                        {DATE_CALCULATIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 16, fontSize: "0.78rem", color: "var(--text-muted)" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(event) => updateField(field.id, { required: event.target.checked })}
                        disabled={allRequired}
                      />
                      Obligatorio
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={field.sensitive}
                        onChange={(event) => updateField(field.id, { sensitive: event.target.checked })}
                      />
                      Dato sensible (se muestra oculto en el visor)
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <button className="btn-secondary" onClick={addField} style={{ marginTop: 10, alignSelf: "flex-start" }}>
              <Plus size={14} /> Agregar campo
            </button>
          </div>

          {duplicateNotice && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                background: "#FEF3C7",
                borderRadius: 8,
                color: "#92400E",
                fontSize: "0.78rem",
              }}
            >
              <AlertCircle size={15} /> {duplicateNotice}
            </div>
          )}

          {error && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                background: "#FEE2E2",
                borderRadius: 8,
                color: "#C53030",
                fontSize: "0.78rem",
              }}
            >
              <AlertCircle size={15} /> {error}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" onClick={handleSave} disabled={!valid || saving}>
            {saving ? <><Loader size={14} className="spin" /> Guardando...</> : "Guardar"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
